import { useEffect, useMemo, useState } from 'react';
import { OFF } from '../lib/off';
import { ApiError, api, patchApi, type PatchRingSetting } from '../api/client';
import { useAuth } from '../lib/auth';
import { COMMON_ZONES, allZones, deviceZone, timeIn, zoneLabel, zoneName, zoneOffset } from '../lib/time';
import { Button, Dialog, Field, Notice } from './ui';
// Voicemail emails (the call records service): your own choices, inside a company.
import { useParams } from 'react-router';
import { VoicemailEmailFields, useVoicemailEmailChoices } from './VoicemailEmailChoices';
// Your settings → Notifications (the installable app).
import { NotificationSettings } from './Notifications';
// Your photo, in Stitch and on desk phones.
import { YourPhoto } from './YourPhoto';
// The chime while waiting for people to join your Patch (1.12.26).
import { previewPatchWaiting } from '../lib/calls';
import { setWaitingSoundOn, waitingSoundOn } from '../lib/patchWaiting';
// The sound for a text to your ring group or queue (1.12.33).
import { TeamTextSoundSettings } from './TeamTextSound';
import { Icon } from '../lib/icons';
import { HeadsetSettings } from './Headset';
import { PasswordSettings } from './Password';

/** A time zone, from the common ones first, then every zone by offset. */
export function TimeZoneSelect({ id, value, onChange, label = 'Time zone', hint }: { id: string; value: string; onChange: (tz: string) => void; label?: string; hint?: string }) {
  const everyZone = useMemo(() => allZones(), []);
  const known = everyZone.some((z) => z.value === value) || COMMON_ZONES.includes(value);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={hint ? `${id}-hint` : undefined}>
        {!known && value && <option value={value}>{`${zoneOffset(value)} · ${zoneLabel(value)}`}</option>}
        <optgroup label="United States and Canada">
          {COMMON_ZONES.map((z) => (
            <option key={z} value={z}>{`${zoneLabel(z)} · ${zoneOffset(z)}`}</option>
          ))}
        </optgroup>
        <optgroup label="Everywhere">
          {everyZone.map((z) => (
            <option key={z.value} value={z.value}>
              {z.label}
            </option>
          ))}
        </optgroup>
      </select>
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

type Choice = 'device' | 'company' | 'other';

/** Calls and audio (1.12.18): whether Patches ring desk phones at this company, and why not. Null outside one. */
function useDeskRingSetting(tenant: string | undefined): PatchRingSetting | null {
  const [v, setV] = useState<PatchRingSetting | null>(null);
  useEffect(() => {
    if (!tenant) return;
    let gone = false;
    patchApi
      .deskRing(tenant)
      .then((r) => !gone && setV(r))
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [tenant]);
  return tenant ? v : null;
}

/**
 * Your own settings: your photo and name, the time zone Stitch shows times in for you, your local
 * time for coworkers, and the team clock (1.12.13). Opened from the bottom of the menu.
 * `companyZone`: the company's, when inside one.
 */
export function YourSettings({ open, onClose, companyZone, companyName }: { open: boolean; onClose: () => void; companyZone?: string; companyName?: string }) {
  const { state, signedIn } = useAuth();
  const user = state?.user;
  // null (or 'device', from before 1.12.13): Automatic. 'company': the company's. Else a zone.
  const current = user?.timeZone && user.timeZone !== 'device' ? user.timeZone : null;
  const here = deviceZone();
  const [name, setName] = useState(user?.name ?? '');
  const [choice, setChoice] = useState<Choice>(current === 'company' ? 'company' : current ? 'other' : 'device');
  const [other, setOther] = useState(current && current !== 'company' ? current : (companyZone ?? here));
  const [share, setShare] = useState(user?.shareLocalTime !== false);
  const [pinned, setPinned] = useState(Boolean(user?.zonePinned));
  const [pin, setPin] = useState(user?.zonePinned ?? user?.sharedZone ?? here);
  const [clock, setClock] = useState(user?.teamClock !== false);
  const [still, setStill] = useState(user?.teamClockStill === true);
  const [deskRing, setDeskRing] = useState(user?.patchDeskRing !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tenant = useParams().tenant;
  const voicemailEmails = useVoicemailEmailChoices(tenant);
  const deskRingHere = useDeskRingSetting(open ? tenant : undefined);
  if (!user) return null;
  const company = companyName ?? 'your company';
  const shared = user.sharedZone && !user.zonePinned ? user.sharedZone : here;

  async function save() {
    setBusy(true);
    setError('');
    try {
      const timeZone = choice === 'company' ? 'company' : choice === 'device' ? null : other;
      const me = await api.updateMe({
        ...(name.trim() && name.trim() !== user!.name ? { name: name.trim() } : {}),
        timeZone,
        shareLocalTime: share,
        zonePinned: pinned ? pin : null,
        teamClock: clock,
        teamClockStill: still,
        patchDeskRing: deskRing
      });
      await voicemailEmails.save();
      signedIn(me);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Your settings"
      subtitle={user.email}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <fieldset className="plain-fieldset">
        <legend className="field-label">Your photo</legend>
        <span className="hint">Shows wherever Stitch shows you, and on desk phones when you call coworkers. It saves straight away.</span>
        <YourPhoto />
      </fieldset>
      <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      {/* Change it while signed in (2988): saves straight away, not with Save. Not in a demo. */}
      {!state?.demo && <PasswordSettings />}
      <fieldset className="plain-fieldset">
        <legend className="field-label">Show times in</legend>
        <label className="radio-card">
          <input type="radio" name="tz-choice" checked={choice === 'device'} onChange={() => setChoice('device')} />
          <span className="cell-stack">
            <strong>Automatic</strong>
            <small>
              This device’s time zone. Now {zoneName(here)}, {timeIn(here)}. It follows you when you travel.
            </small>
          </span>
        </label>
        {companyZone && (
          <label className="radio-card">
            <input type="radio" name="tz-choice" checked={choice === 'company'} onChange={() => setChoice('company')} />
            <span className="cell-stack">
              <strong>{companyName ? `${companyName}’s time zone` : 'Your company’s time zone'}</strong>
              <small>
                {zoneName(companyZone)}, {timeIn(companyZone)}
              </small>
            </span>
          </label>
        )}
        <label className="radio-card">
          <input type="radio" name="tz-choice" checked={choice === 'other'} onChange={() => setChoice('other')} />
          <span className="cell-stack">
            <strong>Another time zone</strong>
            <small>{choice === 'other' ? zoneLabel(other) : 'Choose one'}</small>
          </span>
        </label>
        {choice === 'other' && <TimeZoneSelect id="your-tz" label="Time zone" value={other} onChange={setOther} />}
        {companyZone && <span className="hint">Business hours, holidays and reports always use {company}’s time, and say so.</span>}
      </fieldset>
      <fieldset className="plain-fieldset">
        <legend className="field-label">Your time, for coworkers</legend>
        <label className="check">
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
          <span className="cell-stack">
            <span>Show my local time to coworkers</span>
            <small className="muted">Only people at {company} see it, in Messages and on your card. People at other companies never do.</small>
          </span>
        </label>
        {share && (
          <div className="indent-choices">
            <label className="radio-card">
              <input type="radio" name="tz-share" checked={!pinned} onChange={() => setPinned(false)} />
              <span className="cell-stack">
                <strong>Automatic</strong>
                <small>
                  From the device you used last. Now {zoneName(shared)}
                  {shared === here ? ', from this device' : ''}.
                </small>
              </span>
            </label>
            <label className="radio-card">
              <input type="radio" name="tz-share" checked={pinned} onChange={() => setPinned(true)} />
              <span className="cell-stack">
                <strong>Always use one time zone</strong>
                <small>{pinned ? zoneLabel(pin) : 'Choose one'}</small>
              </span>
            </label>
            {pinned && <TimeZoneSelect id="your-shared-tz" label="Time zone coworkers see" value={pin} onChange={setPin} />}
          </div>
        )}
      </fieldset>
      <fieldset className="plain-fieldset">
        <legend className="field-label">Team clock</legend>
        <label className="check">
          <input type="checkbox" checked={clock} onChange={(e) => setClock(e.target.checked)} />
          <span className="cell-stack">
            <span>Show the team clock in channels</span>
            <small className="muted">The time where each of the channel’s people are, across the top.</small>
          </span>
        </label>
        {clock && (
          <label className="check">
            <input type="checkbox" checked={still} onChange={(e) => setStill(e.target.checked)} />
            <span className="cell-stack">
              <span>Keep it still</span>
              <small className="muted">It also stays still when this device asks for less motion.</small>
            </span>
          </label>
        )}
      </fieldset>
      {/* The chime shows whether or not the desk-ring check answered (2987). */}
      {(deskRingHere || tenant) && (
        <fieldset className="plain-fieldset">
          <legend className="field-label">Calls and audio</legend>
          {deskRingHere && (
            <label className="check">
              <input type="checkbox" checked={deskRing} onChange={(e) => setDeskRing(e.target.checked)} />
              <span className="cell-stack">
                <span>Ring my desk phone when I’m patched in</span>
                <small className="muted">
                  {deskRingHere.available
                    ? 'When a coworker starts a Patch with you, your desk phone rings too (not your mobile). Pick it up to join from your desk phone. Saved with Save.'
                    : (deskRingHere.reason ?? OFF.patchRings)}
                </small>
              </span>
            </label>
          )}
          <PatchWaitingChoice />
        </fieldset>
      )}
      {/* A headset's call and mute buttons (3141): on this browser, for people in a company. */}
      {tenant && state?.surface !== 'admin' && <HeadsetSettings />}
      <VoicemailEmailFields choices={voicemailEmails} />
      {error && <Notice tone="crit">{error}</Notice>}
      {/* Inside a company: a text for your ring group or queue, its sound (saved straight away). */}
      {tenant && state?.surface !== 'admin' && <TeamTextSoundSettings />}
      {/* Stitch Admin's own container: notifications are turned on in the portal. */}
      {state?.surface !== 'admin' && <NotificationSettings />}
    </Dialog>
  );
}

/** The chime while you wait for people to join a Patch you started: on this device, at once, with a Play to hear it. */
function PatchWaitingChoice() {
  const [on, setOn] = useState(waitingSoundOn);
  return (
    <div className="check sound-choice">
      <label className="check grow">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            setWaitingSoundOn(e.target.checked);
            setOn(e.target.checked);
          }}
        />
        <span className="cell-stack">
          <span>Play a soft chime while I wait for people to join my Patch</span>
          <small className="muted">On this device, saved at once. It stops as soon as someone joins.</small>
        </span>
      </label>
      <button type="button" className="link-btn small" onClick={() => void previewPatchWaiting()}>
        <Icon name="play" size={13} /> Play
      </button>
    </div>
  );
}
